class SubscriptionWizard extends HTMLElement {
  connectedCallback() {
    const dataEl = document.getElementById("subscription-wizard-data");
    if (!dataEl) return;

    this.data = JSON.parse(dataEl.textContent);
    this.sellingPlanId = this.data.selling_plan_id;
    this.currentStep = 1;
    this.selectedCategory = null;
    this.selectedSize = null;
    this.selectedProduct = null;
    this.selectedVariant = null;
    this.selectedSecondaryProduct = null;
    this.selectedSecondaryVariant = null;

    // DOM refs
    this.steps = this.querySelectorAll(".sw-step");
    this.progressSteps = this.querySelectorAll(".sw-progress-step");
    this.progressFills = this.querySelectorAll(".sw-progress-fill");
    this.sizesContainer = document.getElementById("sw-sizes");
    this.colorsContainer = document.getElementById("sw-colors");
    this.secondaryColorsContainer = document.getElementById("sw-colors-secondary");
    this.primaryColorsSection = document.getElementById("sw-primary-colors");
    this.secondaryColorsSection = document.getElementById("sw-secondary-colors");
    this.summaryContainer = document.getElementById("sw-summary");
    this.categoryLabel = document.getElementById("sw-category-label");

    // Cart drawer refs
    this.cartDrawer = document.querySelector("cart-drawer");
    this.cartCountBubble = document.querySelector("#cart-icon-bubble");

    // Event delegation
    this.addEventListener("click", (e) => {
      const action = e.target.closest("[data-action]");
      if (!action) return;

      const actionName = action.dataset.action;

      if (actionName === "select-category") {
        this.selectCategory(action.dataset.category);
      } else if (actionName === "select-size") {
        this.selectSize(action.dataset.size);
      } else if (actionName === "select-color") {
        this.selectPrimaryColor(action);
      } else if (actionName === "select-secondary-color") {
        this.selectSecondaryColor(action);
      } else if (actionName === "subscribe") {
        this.subscribe();
      } else if (actionName === "go-back") {
        this.goBack();
      }
    });
  }

  goToStep(n) {
    this.currentStep = n;

    this.steps.forEach((step) => {
      step.classList.toggle("active", +step.dataset.step === n);
    });

    this.progressSteps.forEach((ps, i) => {
      const stepNum = i + 1;
      ps.classList.remove("active", "completed");
      if (stepNum === n) ps.classList.add("active");
      else if (stepNum < n) ps.classList.add("completed");
    });

    this.progressFills.forEach((fill, i) => {
      fill.style.width = i < n - 1 ? "100%" : "0%";
    });
  }

  get category() {
    return this.data.categories[this.selectedCategory];
  }

  get hasSecondary() {
    const cat = this.category;
    return cat && cat.secondary_cases && cat.secondary_cases.length > 0;
  }

  selectCategory(key) {
    this.selectedCategory = key;
    const category = this.category;
    if (!category) return;

    this.categoryLabel.textContent = category.label;

    // Reset secondary selections
    this.selectedSecondaryProduct = null;
    this.selectedSecondaryVariant = null;

    // Use parent product sizes if available, otherwise extract from case variants
    const sizes = [];
    if (category.sizes && category.sizes.length > 0) {
      category.sizes.forEach((title) => {
        sizes.push({ handle: title.toLowerCase().replace(/\//g, "-").replace(/[()]/g, "").replace(/\s+/g, "-").replace(/-+/g, "-").replace(/-$/, ""), title });
      });
    } else {
      category.cases.forEach((c) => {
        c.variants.forEach((v) => {
          if (!sizes.some((s) => s.handle === v.size_handle)) {
            sizes.push({ handle: v.size_handle, title: v.title });
          }
        });
      });
    }

    this.sizesContainer.innerHTML = sizes
      .map(
        (s) =>
          `<button class="sw-size-btn" data-action="select-size" data-size="${s.title}">${s.title}</button>`
      )
      .join("");

    this.goToStep(2);
  }

  selectSize(size) {
    this.selectedSize = size;
    const category = this.category;
    if (!category) return;

    this._sizeHandle = this.toSizeHandle(size);

    // Find primary cases matching this size (exact or starts-with for long variant titles)
    const matchingCases = category.cases.filter((c) =>
      c.variants.some((v) => (v.size_handle === this._sizeHandle || v.size_handle.startsWith(this._sizeHandle)) && v.available)
    );

    // Determine if we need the color step
    const hasSecondary = this.hasSecondary;
    const needsColorPick = matchingCases.length > 1 || hasSecondary;

    if (!needsColorPick && matchingCases.length === 1) {
      // Single primary, no secondary — skip to summary
      this.selectedProduct = matchingCases[0];
      this.selectedVariant = this.findVariant(matchingCases[0], this._sizeHandle);
      this.renderSummary();
      this.goToStep(4);
      return;
    }

    // Setup primary color section
    const primaryTitle = hasSecondary ? "Choose your wedge case color" : "Choose your case color";
    this.primaryColorsSection.querySelector(".sw-step-title").textContent = primaryTitle;

    const casesToShow = matchingCases.length > 0 ? matchingCases : category.cases;
    this.renderColorButtons(this.colorsContainer, casesToShow, "select-color");

    // Setup secondary color section
    if (hasSecondary) {
      this.secondaryColorsSection.style.display = "block";
      // Map size handle for body pillow: Reflux S/M/L → Body Pillow S or M/L
      const secondarySizeHandle = this.mapSecondarySize(this._sizeHandle);
      const secondaryCases = category.secondary_cases.filter((c) =>
        c.variants.some((v) => v.size_handle === secondarySizeHandle && v.available)
      );
      const toShow = secondaryCases.length > 0 ? secondaryCases : category.secondary_cases;
      this.renderColorButtons(this.secondaryColorsContainer, toShow, "select-secondary-color");
      this._secondarySizeHandle = secondarySizeHandle;
    } else {
      this.secondaryColorsSection.style.display = "none";
    }

    // Reset selections
    this.selectedProduct = null;
    this.selectedVariant = null;
    this.selectedSecondaryProduct = null;
    this.selectedSecondaryVariant = null;

    this.goToStep(3);
  }

  renderColorButtons(container, cases, actionName) {
    container.innerHTML = cases
      .map(
        (c) => `
        <div style="text-align:center">
          <button
            class="sw-color-btn"
            data-action="${actionName}"
            data-handle="${c.handle}"
            style="background-color: ${c.swatch.color}"
            title="${c.swatch.label}"
            aria-label="${c.swatch.label}"
          ></button>
          <span class="sw-color-label">${c.swatch.label}</span>
        </div>`
      )
      .join("");
  }

  selectPrimaryColor(btn) {
    const handle = btn.dataset.handle;
    const category = this.category;
    const caseProduct = category.cases.find((c) => c.handle === handle);
    if (!caseProduct) return;

    this.selectedProduct = caseProduct;
    this.selectedVariant = this.findVariant(caseProduct, this._sizeHandle);

    // Highlight selected
    this.colorsContainer.querySelectorAll(".sw-color-btn").forEach((b) => {
      b.classList.toggle("selected", b.dataset.handle === handle);
    });

    this.tryAdvanceToSummary();
  }

  selectSecondaryColor(btn) {
    const handle = btn.dataset.handle;
    const category = this.category;
    const caseProduct = category.secondary_cases.find((c) => c.handle === handle);
    if (!caseProduct) return;

    this.selectedSecondaryProduct = caseProduct;
    this.selectedSecondaryVariant = this.findVariant(caseProduct, this._secondarySizeHandle);

    // Highlight selected
    this.secondaryColorsContainer.querySelectorAll(".sw-color-btn").forEach((b) => {
      b.classList.toggle("selected", b.dataset.handle === handle);
    });

    this.tryAdvanceToSummary();
  }

  tryAdvanceToSummary() {
    // Need primary selection
    if (!this.selectedProduct || !this.selectedVariant) return;

    // If secondary exists, need that too
    if (this.hasSecondary && (!this.selectedSecondaryProduct || !this.selectedSecondaryVariant)) return;

    this.renderSummary();
    this.goToStep(4);
  }

  findVariant(product, sizeHandle) {
    return (
      product.variants.find((v) => v.size_handle === sizeHandle && v.available) ||
      product.variants.find((v) => v.size_handle.startsWith(sizeHandle) && v.available) ||
      product.variants.find((v) => v.size_handle === sizeHandle) ||
      product.variants.find((v) => v.size_handle.startsWith(sizeHandle)) ||
      product.variants[0]
    );
  }

  toSizeHandle(size) {
    return size.toLowerCase().replace(/\//g, "-");
  }

  /**
   * Maps a wedge size handle to the corresponding body pillow size handle.
   * Reflux: small → small, medium → medium-large, large → medium-large
   * Shoulder: small-medium → medium-large, large → medium-large
   * (every Shoulder system pairs with the Medium/Large body pillow case; only Reflux Small uses the Small case)
   */
  mapSecondarySize(primarySizeHandle) {
    const map = {
      "small": "small",
      "small---for-use-with-small-medcline-wedge": "small",
      "medium": "medium-large",
      "medium---for-use-with-medium-medcline-wedge": "medium-large",
      "large": "medium-large",
      "large---for-use-with-large-medcline-wedge": "medium-large",
      "small-medium": "medium-large",
    };
    return map[primarySizeHandle] || primarySizeHandle;
  }

  renderSummary() {
    const items = this.getSummaryItems();
    let totalSubPrice = 0;
    let totalOrigPrice = 0;

    const itemsHtml = items
      .map((item) => {
        const allocation = item.variant.selling_plan_allocations.find(
          (a) => a.selling_plan_id === this.sellingPlanId
        );
        const subPrice = allocation ? allocation.per_delivery_price_raw : item.variant.price_raw;
        const origPrice = item.variant.price_raw;
        totalSubPrice += subPrice;
        totalOrigPrice += origPrice;

        return `
          <div class="sw-summary-item">
            <img class="sw-summary-image" src="${item.product.image}" alt="${item.product.title}" loading="lazy">
            <div>
              <p class="sw-summary-product-name">${item.product.title}</p>
              <p class="sw-summary-meta">Size: ${this.selectedSize}</p>
            </div>
          </div>
        `;
      })
      .join("");

    const showStrike = totalSubPrice < totalOrigPrice;
    const formatMoney = (cents) => {
      const amt = (cents / 100).toFixed(2);
      return "$" + amt.replace(/\.00$/, "");
    };

    const savings = showStrike ? formatMoney(totalOrigPrice - totalSubPrice) : null;

    this.summaryContainer.innerHTML = `
      <div class="sw-summary-items">${itemsHtml}</div>
      <div class="sw-summary-pricing" style="margin-top:16px">
        <span class="sw-summary-sub-price">${formatMoney(totalSubPrice)}</span>
        ${showStrike ? `<span class="sw-summary-orig-price">${formatMoney(totalOrigPrice)}</span>` : ""}
        <span class="sw-summary-frequency">every 6 months</span>
      </div>
      <div class="sw-summary-perks">
        <div class="sw-perk"><span class="sw-perk-icon">✓</span> 20% OFF every delivery${savings ? ` (you save ${savings})` : ""}</div>
        <div class="sw-perk"><span class="sw-perk-icon">✓</span> Free shipping on every refill</div>
        <div class="sw-perk"><span class="sw-perk-icon">✓</span> Cancel or skip anytime</div>
      </div>
      <button
        class="sw-subscribe-btn"
        data-action="subscribe"
      >
        <span class="sw-btn-label">Subscribe Now</span>
        <span class="sw-btn-adding">Adding to cart&hellip;</span>
      </button>
    `;
  }

  getSummaryItems() {
    const items = [{ product: this.selectedProduct, variant: this.selectedVariant }];
    if (this.hasSecondary && this.selectedSecondaryProduct) {
      items.push({ product: this.selectedSecondaryProduct, variant: this.selectedSecondaryVariant });
    }
    return items;
  }

  async subscribe() {
    const btn = this.querySelector(".sw-subscribe-btn");
    if (!btn || btn.disabled) return;

    btn.disabled = true;

    const items = this.getSummaryItems().map((item) => ({
      id: item.variant.id,
      quantity: 1,
      selling_plan: this.sellingPlanId,
    }));

    try {
      await fetch(window.Shopify.routes.root + "cart/add.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ items }),
      });

      await this.openAndPopulateCart();
    } catch (error) {
      console.error("Subscription Wizard | Cart error:", error);
    } finally {
      btn.disabled = false;
    }
  }

  async openAndPopulateCart() {
    const response = await fetch("/cart");
    const responseText = await response.text();
    const newCartMarkup = new DOMParser().parseFromString(
      responseText,
      "text/html"
    );

    const newBubble = newCartMarkup.querySelector("#cart-icon-bubble");
    if (this.cartCountBubble && newBubble) {
      this.cartCountBubble.innerHTML = newBubble.innerHTML;
    }

    if (this.cartDrawer) {
      this.cartDrawer.classList.remove("is-empty");

      const newDrawer = newCartMarkup.querySelector("#CartDrawer");
      if (newDrawer) {
        this.cartDrawer.querySelector("#CartDrawer").replaceWith(newDrawer);
      }

      this.cartDrawer.open();

      if (typeof this.cartDrawer.hideInstallmentsCTA === "function") {
        this.cartDrawer.hideInstallmentsCTA();
      }
    }
  }

  goBack() {
    if (this.currentStep > 1) {
      this.goToStep(this.currentStep - 1);
    }
  }
}

customElements.define("subscription-wizard", SubscriptionWizard);
